import React, {
  ReactElement,
  useState,
  useContext,
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
} from 'react';
import { LOCAL_STORAGE_RESERVATION } from 'constants/local-storage';
import { getItem, setItem } from 'helpers/local-storage';
import { passengerDeclarationsDefault } from 'scenes/reservation/guest-information/guest-information';

interface UseReservations {
  parkId: number;
  parkCountryCode: string;
  startDate: string;
  endDate: string;
  services: AncillarySlotsAncillarySlotPricing;
  passengerDeclarations: PassengerDeclaration[];
  tempUser: any;
  slot: number;
  slotFee: number;
  slotFeeOveride: number;
  category: number;
  errorMessage: string;
  reserveSlotId?: number;
  setParkId: Dispatch<SetStateAction<number>>;
  setParkCountryCode: Dispatch<SetStateAction<string>>;
  setStartDate: Dispatch<SetStateAction<string>>;
  setEndDate: Dispatch<SetStateAction<string>>;
  setServices: Dispatch<SetStateAction<AncillarySlotsAncillarySlotPricing>>;
  setTempUser: Dispatch<SetStateAction<any>>;
  setSlot: Dispatch<SetStateAction<any>>;
  setSlotFee: Dispatch<SetStateAction<any>>;
  setSlotFeeOveride: Dispatch<SetStateAction<any>>;
  setPassengerDeclarations: Dispatch<SetStateAction<PassengerDeclaration[]>>;
  setCategory: Dispatch<SetStateAction<number>>;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  setReserveSlotId: Dispatch<SetStateAction<number>>;
}

const ReservationsContext = createContext({});

const ReservationsProvider = (props: any): ReactElement => {
  const reservation = JSON.parse(getItem(LOCAL_STORAGE_RESERVATION) ?? '{}');

  const defaultUser =
    reservation.tempUser && Object.keys(reservation.tempUser).length > 0
      ? reservation.tempUser
      : undefined;
  const [parkId, setParkId] = useState(reservation.parkId ?? 0);
  const [parkCountryCode, setParkCountryCode] = useState(
    reservation.parkCountryCode ?? 'FI',
  );
  const [startDate, setStartDate] = useState(reservation.startDate ?? '');
  const [endDate, setEndDate] = useState(reservation.endDate ?? '');
  const [services, setServices] = useState(reservation.services ?? {});
  const [passengerDeclarations, setPassengerDeclarations] = useState(
    reservation.passengerDeclarations ?? [passengerDeclarationsDefault],
  );
  const [tempUser, setTempUser] = useState(defaultUser);
  const [slot, setSlot] = useState(reservation.slot ?? 0);
  const [slotFee, setSlotFee] = useState(reservation.slotFee ?? 0);
  const [slotFeeOveride, setSlotFeeOveride] = useState(
    reservation.slotFeeOveride ?? 0,
  );
  const [category, setCategory] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [reserveSlotId, setReserveSlotId] = useState(
    reservation.reserveSlotId ?? undefined,
  );

  useEffect(() => {
    setItem(
      LOCAL_STORAGE_RESERVATION,
      JSON.stringify({
        parkId,
        parkCountryCode,
        startDate,
        endDate,
        services,
        tempUser,
        passengerDeclarations,
        slot,
        slotFee,
        slotFeeOveride,
        category,
        errorMessage,
        reserveSlotId,
      }),
    );
  }, [
    parkId,
    parkCountryCode,
    startDate,
    endDate,
    services,
    tempUser,
    passengerDeclarations,
    slot,
    slotFee,
    slotFeeOveride,
    category,
    errorMessage,
    reserveSlotId,
  ]);

  const values: UseReservations = {
    parkId,
    parkCountryCode,
    endDate,
    startDate,
    services,
    passengerDeclarations,
    tempUser,
    slot,
    slotFee,
    slotFeeOveride,
    category,
    errorMessage,
    reserveSlotId,
    setParkId,
    setParkCountryCode,
    setStartDate,
    setEndDate,
    setServices,
    setPassengerDeclarations,
    setTempUser,
    setSlot,
    setSlotFee,
    setSlotFeeOveride,
    setCategory,
    setErrorMessage,
    setReserveSlotId,
  };

  return <ReservationsContext.Provider value={values} {...props} />;
};

const useReservations = (): UseReservations =>
  useContext(ReservationsContext) as UseReservations;

export { ReservationsProvider, useReservations };
